import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import Login from '@/pages/Login';
import Layout from '@/components/Layout';
import Dashboard from '@/pages/Dashboard';
import LeadShield from '@/pages/LeadShield';
import DealDesk from '@/pages/DealDesk';
import Clients from '@/pages/Clients';
import ListingLegend from '@/pages/ListingLegend';
import ContractClerk from '@/pages/ContractClerk';
import NeighborhoodIntel from '@/pages/NeighborhoodIntel';
import AIEmployee from '@/pages/AIEmployee';
import Pricing from '@/pages/Pricing';
import Landing from '@/pages/Landing';
import CallCommand from '@/pages/CallCommand';
import CallLeads from '@/pages/CallLeads';
import CallSession from '@/pages/CallSession';
import CallPractice from '@/pages/CallPractice';
import CallAnalytics from '@/pages/CallAnalytics';
import '@/App.css';

function LoadingSplash() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--ivory)' }}>
      <div className="hq-eyebrow" style={{ color: 'var(--sage)' }}>Loading HomiHQ…</div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <LoadingSplash />;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
}

// Public "/" route: shows Landing to logged-out visitors; sends logged-in visitors
// to /dashboard while preserving query params (e.g. Stripe checkout redirect).
function PublicHome() {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <LoadingSplash />;
  if (user) return <Navigate to={`/dashboard${location.search}`} replace />;
  return <Landing />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<PublicHome />} />
      <Route path="/login" element={<Login initialMode="login" />} />
      <Route path="/register" element={<Login initialMode="register" />} />
      <Route path="/pricing" element={<Pricing />} />

      {/* Protected app shell */}
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/ai" element={<AIEmployee />} />
        <Route path="/call" element={<CallCommand />} />
        <Route path="/call/leads" element={<CallLeads />} />
        <Route path="/call/session/:id" element={<CallSession />} />
        <Route path="/call/practice" element={<CallPractice />} />
        <Route path="/call/analytics" element={<CallAnalytics />} />
        <Route path="/leads" element={<LeadShield />} />
        <Route path="/deals" element={<DealDesk />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/listings" element={<ListingLegend />} />
        <Route path="/contracts" element={<ContractClerk />} />
        <Route path="/intel" element={<NeighborhoodIntel />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
